import { Box } from '@mui/material';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SolaForceDataGrid,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import {
  UserActivityChangeEventsRow,
  useActivityLogDataTranslations,
} from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data';
import { EmployeeChangeActivityDataContent } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/EmployeeChangeActivityDataContent';
import {
  UseEmployeeChangesActivityLogProps,
  useEmployeeChangesActivityLog,
} from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/useEmployeeChangesActivityLog';
import { useSxStyles } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/useStyles';

type PropsType = UseEmployeeChangesActivityLogProps;

export const EmployeeChangeActivityLog = (props: PropsType) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { rows } = useEmployeeChangesActivityLog(props);

  const {
    getAuditTypeTranslation,
    getAuditTargetTypeTranslation,
    getAuditOperationTranslation,
  } = useActivityLogDataTranslations();

  const { columnsConfig, columnGroupingConfig } = useMemo(() => {
    const userColumnsConfig: GridColDef<UserActivityChangeEventsRow>[] = [
      {
        field: 'userName',
        headerName: t('name'),
        minWidth: 150,
        valueGetter: (value) => {
          return value === 'SYSTEM' ? t('laSystem') : value;
        },
      },
      {
        field: 'userUnit',
        headerName: t('laUnit'),
        minWidth: 150,
      },
    ];
    const activityColumnsConfig: GridColDef<UserActivityChangeEventsRow>[] = [
      {
        field: 'targetType',
        headerName: t('userActivityTargetType'),
        minWidth: 120,
        valueGetter: (_value, row) => getAuditTargetTypeTranslation(row),
      },
      {
        field: 'auditType',
        headerName: t('userActivityTargetResource'),
        minWidth: 200,
        valueGetter: (_value, row) => getAuditTypeTranslation(row),
      },
      {
        field: 'operation',
        headerName: t('userActivityEventType'),
        minWidth: 60,
        valueGetter: (value) => getAuditOperationTranslation(value),
      },
      {
        field: 'data',
        headerName: t('laDetails'),
        valueGetter: (value) => JSON.stringify(value),
        width: 300,
        flex: 1,
        renderCell: ({ row }) => {
          return <EmployeeChangeActivityDataContent row={row} />;
        },
      },
    ];
    const serverColumnsConfig: GridColDef[] = [
      {
        field: 'timestamp',
        type: 'dateTime',
        headerName: t('serverTime'),
        minWidth: 160,
        renderCell: ({ value }) => {
          return moment(value).format('L LTS');
        },
      },
    ];

    const columnsConfig: GridColDef[] = [
      ...userColumnsConfig,
      ...activityColumnsConfig,
      ...serverColumnsConfig,
    ];

    const columnGroupingConfig: GridColumnGroupingModel = [
      {
        groupId: t('user'),
        children: getFieldNamesFromColumnConfig(userColumnsConfig),
      },
      {
        groupId: t('activity'),
        children: getFieldNamesFromColumnConfig(activityColumnsConfig),
      },
      {
        groupId: t('server'),
        children: getFieldNamesFromColumnConfig(serverColumnsConfig),
      },
    ];

    return {
      columnsConfig,
      columnGroupingConfig,
    };
  }, [
    t,
    getAuditTypeTranslation,
    getAuditTargetTypeTranslation,
    getAuditOperationTranslation,
  ]);

  return (
    <SolaForceDataGrid
      columns={columnsConfig}
      rows={rows}
      columnGroupingModel={columnGroupingConfig}
      hideFooter={true}
      pinnedColumns={{
        left: ['__detail_panel_toggle__', 'userName', 'userUnit'],
      }}
      initialState={{ density: 'compact' }}
      getRowClassName={(params) =>
        params.row.userName === 'SYSTEM' ? 'system-entry' : ''
      }
      getDetailPanelContent={({ row }) => (
        <Box sx={sxStyles.panelContent}>
          <EmployeeChangeActivityDataContent row={row} isPanel={true} />
        </Box>
      )}
      getDetailPanelHeight={(_params) => 'auto'}
      sx={sxStyles.dataGrid}
    />
  );
};
